import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import SearchService, { TypeaheadType } from '~/services/SearchService';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

const SEARCH_TYPES: Record<TypeaheadType, boolean> = {
  airport: false,
  province_state: true,
  neighborhood: true,
  city: true,
  high_level_region: true,
  country: true,
  multi_city_vicinity: true,
  metro_station: false,
  continent: true,
  train_station: false,
  point_of_interest: true,
  colloquial_area: true,
  le_property: true,
  le_property_unique_stays: true,
  bedbank_property: true,
  bedbank_property_unique_stays: true,
  le_experience: false,
  channel_experience: false,
  car_hire_location: false,
};

function AgentHubCommissionsLocationInput({ setCreatedConditions, existingConditions }: Props) {
  const { tenant } = useCurrentTenant();
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [result, setResult] = useState<CommissionOption>([]);
  const [selectedLocations, setSelectedLocations] = useState<CommissionOption>(existingConditions || []);
  const [noOptionsText, setNoOptionsText] = useState<string>('Start typing to search for locations');

  const selectedTypes: Array<TypeaheadType> = Object.keys(SEARCH_TYPES).filter(
    (type) => SEARCH_TYPES[type],
  ) as Array<TypeaheadType>;

  const debounceGetLocations = useMemo(
    () =>
      debounce((search: string) => {
        if (!search) {
          setResult([]);
          return;
        }
        setLoadingState('loading');
        SearchService.typeahead(search, {
          type: selectedTypes,
          brand: ensureAgentHubBrands(tenant.brand),
          region: 'AU',
          limit: 10,
        })
          .then((response) => {
            setResult(
              response.result.results.map((location) => ({
                value: location.primary_text,
                label: location.primary_text,
              })),
            );
            setLoadingState('success');
          })
          .catch(() => {
            setResult([]);
            setNoOptionsText('Failed to fetch location');
          });
      }, 300),
    [selectedTypes],
  );

  const handleSaveCondition = () => {
    setCreatedConditions(
      {
        type: 'location',
        value: selectedLocations,
      },
      true,
    );
  };

  useEffect(() => {
    handleSaveCondition();
  }, [selectedLocations]);

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (!newInputValue) {
      setNoOptionsText('Start typing to search for locations');
      return;
    }
    debounceGetLocations(newInputValue);
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedLocations(value);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={loadingState === 'loading'}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label="Location" placeholder={selectedLocations.length > 0 ? '' : 'Location'} />
          )}
          onInputChange={handleInputChange}
          value={selectedLocations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for locations
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsLocationInput;
